import React, { useEffect, useState, useMemo } from 'react';
import { useQueryClient } from 'react-query';

import Flex from 'modules/shared/components/general/Flex/Flex';
import Spinner from 'modules/shared/components/general/Spinner/Spinner';
import InfiniteScroll from 'modules/shared/components/general/InfiniteScroll/InfiniteScroll';

import EmailBlock from 'modules/ClientCommunication/components/EmailBlock/EmailBlock';
import EmptyState from 'modules/ClientCommunication/components/EmptyState/EmptyState';
import { useClientCommunicationContext } from 'context/clientCommunication.context';

import { servicesNames } from 'constants/servicesNames.constants';
import { AppThemeConstants } from 'constants/style.constants';

const MonthlyBoard: React.FC = () => {
  const queryClient = useQueryClient();
  const [cstWeekday, setCSTWeekday] = useState<string>("");
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const {
    activeTab,

    inboxData,
    inboxFetchNextPage,
    inboxHasNextPage,
    inboxIsFetching,
    inboxIsFetchingNextPage,

    performanceUpdateDoneData,
    performanceUpdateDoneFetchNextPage,
    performanceUpdateDoneHasNextPage,
    performanceUpdateDoneIsFetching,
    performanceUpdateDoneIsFetchingNextPage,

    doneData,
    doneFetchNextPage,
    doneHasNextPage,
    doneIsFetching,
    doneIsFetchingNextPage,

    isFetchingFsms,
  } = useClientCommunicationContext();

  const inboxDataCount = inboxData?.pages[0].data.count;
  const performanceUpdateDoneDataCount = performanceUpdateDoneData?.pages[0].data.count;
  const doneDataCount = doneData?.pages[0].data.count;

  const emailTypes = [
    {
      type: "inbox",
      title: "Inbox",
      color: AppThemeConstants.blue1,
    },
    {
      type: "performance_update_done",
      title: "Performance Update Done",
      color: AppThemeConstants.orange,
    },
    {
      type: "done",
      title: "Done",
      color: AppThemeConstants.green,
    },
  ];

  const fetchNextPageFunction = useMemo(() => {
    if (inboxHasNextPage) {
      return inboxFetchNextPage;
    } else if (performanceUpdateDoneHasNextPage) {
      return performanceUpdateDoneFetchNextPage;
    } else if (doneHasNextPage) {
      return doneFetchNextPage;
    }
    return () => undefined;
  }, [doneFetchNextPage, doneHasNextPage, inboxFetchNextPage, inboxHasNextPage, performanceUpdateDoneFetchNextPage, performanceUpdateDoneHasNextPage]);

  useEffect(() => {
    const cstTime = new Date().toLocaleString("en-US", {
      timeZone: "America/Chicago",
    });

    const cstDate = new Date(cstTime);
    const weekdays = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const weekday = weekdays[cstDate.getDay()];

    setCSTWeekday(weekday);

    const services = [
      servicesNames.getInboxEmailReportsData,
      servicesNames.getPerformanceUpdateDoneEmailReportsData,
      servicesNames.getDoneEmailReportsData,
    ];

    services.forEach((service) => {
      queryClient.removeQueries([service, { activeTab }]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenClientCommunicationDr = () => setIsDrawerOpen(true);

  const handleCloseClientCommunicationDr = () => setIsDrawerOpen(false);

  const generateContent = () => {
    if (
      (inboxIsFetching && !inboxIsFetchingNextPage) ||
      (performanceUpdateDoneIsFetching && !performanceUpdateDoneIsFetchingNextPage) ||
      (doneIsFetching && !doneIsFetchingNextPage) || isFetchingFsms
    ) {
      return (
        <Flex justifyContent="center">
          <Spinner style={{ margin: "40px" }} />
        </Flex>
      );
    }
    if (inboxDataCount || performanceUpdateDoneDataCount || doneDataCount) {
      return (
        <>
          <Flex justifyContent="space-between" style={{ marginTop: "24px" }}>
            {emailTypes?.map(({ title, color, type }) => (
              <EmailBlock
                key={title}
                title={title}
                color={color}
                type={type}
                weekday={cstWeekday}
                handleCloseClientCommunicationDr={handleCloseClientCommunicationDr}
                handleOpenClientCommunicationDr={handleOpenClientCommunicationDr}
                isDrawerOpen={isDrawerOpen}
              />
            ))}
          </Flex>
          {(inboxIsFetchingNextPage ||
            performanceUpdateDoneIsFetchingNextPage ||
            doneIsFetchingNextPage) && (
            <Flex
              justifyContent="center"
              style={{ width: "100%", marginBottom: "40px" }}
            >
              <Spinner  />
            </Flex>
          )}
        </>
      );
    }
    if (
      !inboxDataCount && !performanceUpdateDoneDataCount && !doneDataCount
    ) {
      return <EmptyState />;
    }
    return null;
  };

  return (
    <InfiniteScroll
      scroll
      next={() => fetchNextPageFunction()}
      maxHeight={'calc(100vh - 300px)'}
      hasMore={
        !(inboxIsFetchingNextPage ||
          performanceUpdateDoneIsFetchingNextPage ||
          doneIsFetchingNextPage)
      }
      isOverflowHide={isDrawerOpen}
    >
      {generateContent()}
    </InfiniteScroll>
  );
};

export default MonthlyBoard;
